/**
 * My Courses - enrolled course journey overview.
 *
 * This section treats a course as an object with identity, but the content is
 * driven by learner state: next lesson, completion, remaining work, and recent
 * activity.
 */
import { type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useMyEnrolments } from '@/shared/api/hooks';
import type { EnrolmentWithCourse } from '@formency/shared';
import { formatRelativeTime } from '@/shared/utils/formatRelativeTime';
import { meta, sectionHeading } from '../styles';

const MAX_VISIBLE = 3;
const PATH_NODE_COUNT = 7;

type MyCoursesCardVariant = 'default' | 'quiet';

interface MyCoursesCardProps {
  variant?: MyCoursesCardVariant;
}

type CourseSummary = EnrolmentWithCourse['courses'] & {
  accentColour?: string | null;
};

interface CourseJourney {
  completed: number;
  total: number;
  percentage: number;
  complete: boolean;
  remaining: number;
  nextLesson: number | null;
  stateLabel: string;
  nextLabel: string;
  completionLabel: string;
  remainingLabel: string;
  activityLabel: string;
}

function sortForDashboard(enrolments: EnrolmentWithCourse[]): EnrolmentWithCourse[] {
  return [...enrolments].sort((a, b) => {
    const aTime = new Date(
      a.lastAccessedAt ?? a.startedAt ?? a.enrolledAt ?? a.createdAt ?? 0
    ).getTime();
    const bTime = new Date(
      b.lastAccessedAt ?? b.startedAt ?? b.enrolledAt ?? b.createdAt ?? 0
    ).getTime();
    return bTime - aTime;
  });
}

function clampProgress(value: number): number {
  return Math.max(0, Math.min(100, Math.round(value)));
}

function activityLabel(enrolment: EnrolmentWithCourse): string {
  if (enrolment.lastAccessedAt) return `Opened ${formatRelativeTime(enrolment.lastAccessedAt)}`;
  if (enrolment.startedAt) return `Started ${formatRelativeTime(enrolment.startedAt)}`;
  if (enrolment.enrolledAt) return `Enrolled ${formatRelativeTime(enrolment.enrolledAt)}`;
  return 'Available now';
}

function journeyFor(enrolment: EnrolmentWithCourse): CourseJourney {
  const total = enrolment.progress?.totalLessons ?? 0;
  const rawCompleted = enrolment.progress?.completedLessons ?? 0;
  const completed = total > 0 ? Math.min(rawCompleted, total) : rawCompleted;
  const percentage = clampProgress(enrolment.progress?.percentage ?? 0);
  const complete = percentage >= 100 || !!enrolment.completedAt;
  const remaining = total > 0 ? Math.max(total - completed, 0) : 0;
  const nextLesson = !complete && total > 0 ? Math.min(completed + 1, total) : null;
  const started = completed > 0 || !!enrolment.startedAt || !!enrolment.lastAccessedAt;

  return {
    completed,
    total,
    percentage,
    complete,
    remaining,
    nextLesson,
    stateLabel: complete ? 'Completed' : started ? 'In progress' : 'Ready to start',
    nextLabel: complete
      ? 'Course complete'
      : nextLesson
        ? `Next lesson ${nextLesson}`
        : 'Course path ready',
    completionLabel: total > 0 ? `${completed}/${total} complete` : `${completed} complete`,
    remainingLabel:
      total === 0
        ? 'Lessons coming soon'
        : complete
          ? 'All lessons done'
          : `${remaining} ${remaining === 1 ? 'lesson' : 'lessons'} left`,
    activityLabel: activityLabel(enrolment),
  };
}

function courseVars(course: CourseSummary): CSSProperties {
  return {
    '--course-accent': course.accentColour ?? 'var(--color-accent)',
  } as CSSProperties;
}

function pathNodeClass(index: number, journey: CourseJourney): string {
  if (journey.complete) return 'is-complete';

  const completedNodes =
    journey.total > 0
      ? Math.floor((journey.completed / journey.total) * PATH_NODE_COUNT)
      : Math.floor((journey.percentage / 100) * PATH_NODE_COUNT);
  const activeIndex = Math.min(PATH_NODE_COUNT - 1, Math.max(0, completedNodes));

  if (index < completedNodes) return 'is-complete';
  if (index === activeIndex) return 'is-active';
  return '';
}

function ProgressLine({
  enrolment,
  compact = false,
}: {
  enrolment: EnrolmentWithCourse;
  compact?: boolean;
}) {
  const journey = journeyFor(enrolment);
  const visibleWidth = journey.percentage > 0 ? Math.max(journey.percentage, compact ? 8 : 3) : 0;

  return (
    <span className={compact ? 'course-progress course-progress--compact' : 'course-progress'}>
      <span style={{ width: `${visibleWidth}%` }} />
    </span>
  );
}

function CourseCover({
  enrolment,
  compact = false,
}: {
  enrolment: EnrolmentWithCourse;
  compact?: boolean;
}) {
  const course = enrolment.courses;
  const journey = journeyFor(enrolment);
  const imageUrl = course.headerImageUrl ?? course.iconUrl;

  return (
    <span className={compact ? 'course-cover course-cover--compact' : 'course-cover'} aria-hidden>
      {imageUrl ? (
        <img src={imageUrl} alt="" className="course-cover__image" />
      ) : (
        <span className="course-cover__path">
          {Array.from({ length: PATH_NODE_COUNT }).map((_, index) => (
            <span key={index} className={`course-cover__node ${pathNodeClass(index, journey)}`} />
          ))}
        </span>
      )}
      <span className="course-cover__percent">{journey.percentage}%</span>
    </span>
  );
}

function CourseSpotlight({ enrolment }: { enrolment: EnrolmentWithCourse }) {
  const course = enrolment.courses as CourseSummary;
  const journey = journeyFor(enrolment);

  return (
    <Link to={`/courses/${course.id}`} className="course-spotlight" style={courseVars(course)}>
      <CourseCover enrolment={enrolment} />

      <span className="course-spotlight__main">
        <span className="course-kicker">{journey.stateLabel}</span>
        <span className="course-spotlight__title">{course.title}</span>

        <span className="course-spotlight__next">
          <strong>{journey.nextLabel}</strong>
          {journey.nextLesson && journey.total > 0 ? <span>of {journey.total}</span> : null}
        </span>

        <span className="course-facts" aria-label="Course progress summary">
          <span>
            <strong>{journey.completionLabel}</strong>
            <span>lessons</span>
          </span>
          <span>
            <strong>{journey.remainingLabel}</strong>
            <span>remaining</span>
          </span>
          <span>
            <strong>{journey.activityLabel}</strong>
            <span>activity</span>
          </span>
        </span>

        <ProgressLine enrolment={enrolment} />
      </span>

      <span className="course-spotlight__action">
        Open course <ArrowRight size={15} strokeWidth={2} />
      </span>
    </Link>
  );
}

function CourseMiniCard({ enrolment }: { enrolment: EnrolmentWithCourse }) {
  const course = enrolment.courses as CourseSummary;
  const journey = journeyFor(enrolment);

  return (
    <Link to={`/courses/${course.id}`} className="course-mini-card" style={courseVars(course)}>
      <CourseCover enrolment={enrolment} compact />

      <span className="course-mini-card__body">
        <span className="course-mini-card__title">{course.title}</span>
        <span className="course-mini-card__next">{journey.nextLabel}</span>
        <ProgressLine enrolment={enrolment} compact />
        <span className="course-mini-card__meta">
          {journey.remainingLabel} - {journey.activityLabel}
        </span>
      </span>

      <span className="course-mini-card__metric">
        <strong>{journey.percentage}%</strong>
        <span>{journey.completionLabel}</span>
      </span>

      <ArrowRight className="course-mini-card__arrow" size={15} strokeWidth={2} aria-hidden />
    </Link>
  );
}

export default function MyCoursesCard({ variant = 'default' }: MyCoursesCardProps) {
  const { data: enrolments } = useMyEnrolments();
  const isQuiet = variant === 'quiet';

  if (!enrolments?.length) return null;

  const sorted = sortForDashboard(enrolments);
  const [lead, ...supporting] = sorted.slice(0, MAX_VISIBLE);

  if (!lead) return null;

  return (
    <section className={isQuiet ? 'course-shelf course-shelf--quiet' : 'course-shelf'}>
      <div className="course-shelf__header">
        {isQuiet ? (
          <div className="course-shelf__filter-wrap" aria-label="Course filter">
            <span className="course-shelf__filter-pill">In progress</span>
            <span className="course-shelf__count" style={{ ...meta, fontSize: 'var(--text-sm)' }}>
              {enrolments.length} {enrolments.length === 1 ? 'course' : 'courses'}
            </span>
          </div>
        ) : (
          <div className="course-shelf__title-wrap">
            <h2 style={sectionHeading}>My Courses</h2>
            <span style={{ ...meta, fontSize: 'var(--text-sm)' }}>{enrolments.length}</span>
          </div>
        )}
        <Link to="/my-courses" className="course-shelf__browse">
          Browse all <ArrowRight size={13} strokeWidth={2} />
        </Link>
      </div>

      <div className="course-shelf__stack">
        <CourseSpotlight enrolment={lead} />
        {supporting.length > 0 && (
          <div className="course-mini-grid">
            {supporting.map((enrolment) => (
              <CourseMiniCard key={enrolment.courseId} enrolment={enrolment} />
            ))}
          </div>
        )}
      </div>

      <style>{`
        .course-shelf {
          display: flex;
          min-width: 0;
          flex-direction: column;
          gap: 15px;
        }

        .course-shelf__header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding: 0 2px;
        }

        .course-shelf__title-wrap {
          display: flex;
          min-width: 0;
          align-items: baseline;
          gap: 10px;
        }

        .course-shelf__filter-wrap {
          display: inline-flex;
          min-width: 0;
          align-items: center;
          gap: 10px;
        }

        .course-shelf__filter-pill {
          display: inline-flex;
          min-height: 34px;
          align-items: center;
          padding: 6px 13px;
          border: 1px solid var(--dashboard-divider);
          border-radius: var(--radius-full);
          background: color-mix(in srgb, var(--color-surface-solid) 72%, transparent);
          color: var(--color-text-secondary);
          font-size: var(--type-meta-size);
          font-weight: var(--weight-semibold);
          line-height: 1;
        }

        .course-shelf__count {
          white-space: nowrap;
        }

        .course-shelf__browse {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          min-height: 34px;
          padding: 6px 13px;
          border-radius: var(--radius-full);
          background: color-mix(in srgb, var(--color-accent-subtle) 74%, transparent);
          color: var(--color-accent-text);
          font-size: var(--type-meta-size);
          font-weight: var(--weight-semibold);
          text-decoration: none;
          white-space: nowrap;
          transition:
            background var(--motion-fast) var(--motion-ease-out),
            transform var(--motion-fast) var(--motion-ease-out);
        }

        .course-shelf__browse:hover {
          background: var(--color-accent-subtle);
          transform: translateY(-1px);
        }

        .course-shelf__stack {
          display: flex;
          min-width: 0;
          flex-direction: column;
          gap: 12px;
        }

        .course-spotlight,
        .course-mini-card {
          color: var(--color-text);
          text-decoration: none;
        }

        .course-spotlight {
          display: grid;
          grid-template-columns: auto minmax(0, 1fr) auto;
          align-items: center;
          gap: 22px;
          min-height: 168px;
          padding: 18px 20px;
          border: 1px solid var(--dashboard-divider);
          border-radius: var(--dashboard-panel-radius);
          background:
            linear-gradient(
              115deg,
              color-mix(in srgb, var(--course-accent) 8%, transparent) 0%,
              color-mix(in srgb, var(--color-surface-solid) 72%, transparent) 42%,
              color-mix(in srgb, var(--color-surface-solid) 92%, transparent) 100%
            );
          box-shadow: var(--dashboard-panel-shadow);
          transition:
            border-color var(--motion-fast) var(--motion-ease-out),
            transform var(--motion-fast) var(--motion-ease-out),
            box-shadow var(--motion-fast) var(--motion-ease-out);
        }

        .course-spotlight:hover {
          border-color: var(--color-accent-line);
          box-shadow: 0 22px 54px rgba(45, 36, 65, 0.09);
          transform: translateY(-1px);
        }

        .course-shelf--quiet .course-spotlight {
          min-height: 132px;
          padding: 15px 17px;
          background: color-mix(in srgb, var(--color-surface-solid) 74%, transparent);
          box-shadow: var(--shadow-sm);
        }

        .course-shelf--quiet .course-spotlight:hover {
          box-shadow: var(--dashboard-panel-shadow);
        }

        .course-cover {
          position: relative;
          display: flex;
          width: 116px;
          height: 116px;
          flex-shrink: 0;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          border-radius: 22px;
          border: 1px solid color-mix(in srgb, var(--course-accent) 20%, var(--color-surface-border));
          background:
            radial-gradient(
              circle at 24% 20%,
              color-mix(in srgb, var(--course-accent) 16%, transparent),
              transparent 42%
            ),
            linear-gradient(
              135deg,
              color-mix(in srgb, var(--color-surface-solid) 88%, var(--course-accent)) 0%,
              color-mix(in srgb, var(--color-surface-muted) 72%, var(--course-accent)) 100%
            );
        }

        .course-cover--compact {
          width: 70px;
          height: 70px;
          border-radius: 18px;
        }

        .course-shelf--quiet .course-spotlight .course-cover {
          width: 88px;
          height: 88px;
          border-radius: 18px;
        }

        .course-cover__image {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .course-cover__path {
          display: grid;
          width: 64%;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 8px;
          transform: rotate(-8deg);
        }

        .course-cover--compact .course-cover__path {
          gap: 5px;
        }

        .course-cover__node {
          aspect-ratio: 1;
          border-radius: 999px;
          border: 1px solid color-mix(in srgb, var(--course-accent) 28%, var(--color-border));
          background: color-mix(in srgb, var(--color-surface-solid) 72%, transparent);
          box-shadow: inset 0 0.5px 0 var(--color-surface-highlight);
        }

        .course-cover__node.is-complete {
          border-color: transparent;
          background: var(--course-accent);
        }

        .course-cover__node.is-active {
          border: 2px solid var(--course-accent);
          background: var(--color-surface-solid);
          box-shadow: 0 0 0 5px color-mix(in srgb, var(--course-accent) 14%, transparent);
        }

        .course-cover__percent {
          position: absolute;
          left: 12px;
          bottom: 12px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 42px;
          min-height: 30px;
          padding: 2px 9px;
          border-radius: var(--radius-full);
          background: color-mix(in srgb, var(--color-surface-solid) 86%, transparent);
          color: color-mix(in srgb, var(--course-accent) 72%, var(--color-text));
          font-size: var(--text-sm);
          font-weight: var(--weight-bold);
          line-height: 1;
          box-shadow: var(--shadow-sm);
        }

        .course-cover--compact .course-cover__percent {
          left: 7px;
          bottom: 7px;
          min-width: 34px;
          min-height: 24px;
          padding: 1px 7px;
          font-size: var(--text-xs);
        }

        .course-spotlight__main {
          display: flex;
          min-width: 0;
          flex-direction: column;
        }

        .course-kicker {
          display: inline-flex;
          width: fit-content;
          color: var(--color-accent-text);
          font-size: var(--type-meta-size);
          font-weight: var(--weight-semibold);
          letter-spacing: var(--tracking-eyebrow);
          line-height: 1.2;
          text-transform: uppercase;
        }

        .course-spotlight__title,
        .course-mini-card__title {
          display: block;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-family: var(--font-display);
          font-weight: var(--weight-semibold);
          letter-spacing: var(--tracking-normal);
          color: var(--color-text);
        }

        .course-spotlight__title {
          margin-top: 7px;
          font-size: var(--text-2xl);
          line-height: var(--leading-tight);
        }

        .course-shelf--quiet .course-spotlight__title {
          margin-top: 5px;
          font-size: var(--text-xl);
        }

        .course-spotlight__next {
          display: inline-flex;
          min-width: 0;
          align-items: baseline;
          gap: 7px;
          margin-top: 6px;
          color: var(--color-text-secondary);
          font-size: var(--text-base);
          font-weight: var(--weight-medium);
          line-height: var(--leading-snug);
        }

        .course-spotlight__next strong {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-weight: var(--weight-semibold);
        }

        .course-spotlight__next span {
          color: var(--color-text-muted);
          font-size: var(--type-meta-size);
          font-weight: var(--weight-medium);
        }

        .course-facts {
          display: flex;
          flex-wrap: wrap;
          gap: 14px;
          margin-top: 16px;
        }

        .course-shelf--quiet .course-facts {
          margin-top: 11px;
        }

        .course-facts > span {
          display: flex;
          min-width: 0;
          flex-direction: column;
          gap: 2px;
        }

        .course-facts strong {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          color: var(--color-text);
          font-size: var(--text-sm);
          font-weight: var(--weight-semibold);
          line-height: 1.2;
        }

        .course-facts span span {
          color: var(--color-text-muted);
          font-size: var(--text-xs);
          font-weight: var(--weight-medium);
          line-height: 1.2;
        }

        .course-progress {
          display: block;
          width: min(680px, 100%);
          height: 7px;
          margin-top: 17px;
          overflow: hidden;
          border-radius: var(--radius-full);
          background: color-mix(in srgb, var(--color-surface-muted) 70%, transparent);
        }

        .course-shelf--quiet .course-progress {
          margin-top: 12px;
        }

        .course-progress--compact {
          height: 5px;
          margin-top: 9px;
        }

        .course-progress > span {
          display: block;
          height: 100%;
          border-radius: inherit;
          background: linear-gradient(
            90deg,
            var(--course-accent),
            color-mix(in srgb, var(--course-accent) 56%, var(--color-info))
          );
          transition: width 0.6s var(--motion-ease-out);
        }

        .course-spotlight__action {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          min-height: 44px;
          padding: 8px 16px;
          border-radius: var(--radius-full);
          background: var(--color-accent);
          color: var(--color-text-on-accent);
          font-size: var(--type-meta-size);
          font-weight: var(--weight-semibold);
          white-space: nowrap;
          box-shadow: 0 12px 26px rgba(135, 96, 211, 0.18);
        }

        .course-shelf--quiet .course-spotlight__action {
          background: color-mix(in srgb, var(--color-accent-subtle) 82%, transparent);
          color: var(--color-accent-text);
          box-shadow: none;
        }

        .course-mini-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 12px;
        }

        .course-mini-card {
          display: grid;
          grid-template-columns: auto minmax(0, 1fr) auto auto;
          align-items: center;
          gap: 14px;
          min-height: 102px;
          padding: 13px 14px;
          border: 1px solid var(--dashboard-divider);
          border-radius: var(--dashboard-panel-radius);
          background: color-mix(in srgb, var(--color-surface-solid) 68%, transparent);
          box-shadow: var(--shadow-sm);
          transition:
            border-color var(--motion-fast) var(--motion-ease-out),
            background var(--motion-fast) var(--motion-ease-out),
            transform var(--motion-fast) var(--motion-ease-out);
        }

        .course-mini-card:hover {
          border-color: var(--color-accent-line);
          background: color-mix(in srgb, var(--color-surface-solid) 86%, transparent);
          transform: translateY(-1px);
        }

        .course-mini-card__body {
          display: flex;
          min-width: 0;
          flex-direction: column;
        }

        .course-mini-card__title {
          font-size: var(--text-sm);
          line-height: var(--leading-snug);
        }

        .course-mini-card__next,
        .course-mini-card__meta {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-size: var(--text-xs);
          font-weight: var(--weight-medium);
        }

        .course-mini-card__next {
          margin-top: 3px;
          color: var(--color-text-secondary);
        }

        .course-mini-card__meta {
          margin-top: 6px;
          color: var(--color-text-muted);
        }

        .course-mini-card__metric {
          display: flex;
          min-width: 66px;
          flex-direction: column;
          align-items: flex-end;
          gap: 1px;
          text-align: right;
        }

        .course-mini-card__metric strong {
          color: var(--color-text);
          font-family: var(--font-display);
          font-size: var(--text-xl);
          font-weight: var(--weight-bold);
          line-height: 1;
        }

        .course-mini-card__metric span {
          color: var(--color-text-muted);
          font-size: 0.75rem;
          font-weight: var(--weight-medium);
          line-height: 1.2;
        }

        .course-mini-card__arrow {
          color: var(--color-accent-text);
        }

        @media (max-width: 1180px) {
          .course-spotlight {
            grid-template-columns: auto minmax(0, 1fr);
          }

          .course-spotlight__action {
            grid-column: 2;
            justify-self: start;
          }

          .course-mini-card {
            grid-template-columns: auto minmax(0, 1fr) auto;
          }

          .course-mini-card__metric {
            display: none;
          }
        }

        @media (max-width: 720px) {
          .course-shelf__header {
            align-items: flex-start;
          }

          .course-spotlight {
            grid-template-columns: 1fr;
            padding: 16px;
          }

          .course-cover {
            width: 92px;
            height: 92px;
          }

          .course-spotlight__action {
            grid-column: auto;
          }

          .course-mini-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </section>
  );
}
